import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { SITE_URL } from '@/components/base/SEO';

export default function ProsCons() {
  return (
    <main>
      <SEO
        title="Litbuy Spreadsheet Pros and Cons: Honest 2026 Review | LitbuySpreadsheets.online"
        description="Balanced assessment of Litbuy Spreadsheet strengths and limitations. See how it performs on ease of use, features, scalability, and overall value for e-commerce sellers."
        canonicalPath="/litbuy-spreadsheet-pros-cons"
        keywords="litbuy spreadsheet pros and cons, litbuy spreadsheet review, litbuy spreadsheet honest review, spreadsheet evaluation, e-commerce tools comparison"
        schemas={[
          {
            '@type': 'Article',
            headline: 'Litbuy Spreadsheet Pros and Cons: Honest Review',
            description: 'A balanced assessment of Litbuy Spreadsheet strengths and limitations. See how it performs on ease of use, features, scalability, and overall value for e-commerce sellers.',
            author: { '@type': 'Organization', name: 'Litbuy Editorial Team' },
            publisher: { '@type': 'Organization', name: 'Litbuy Spreadsheets', url: SITE_URL },
            datePublished: '2026-06-15',
            dateModified: '2026-06-20',
            mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/litbuy-spreadsheet-pros-cons` },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Litbuy Spreadsheet Pros and Cons', item: `${SITE_URL}/litbuy-spreadsheet-pros-cons` },
            ],
          },
        ]}
      />
      <Navbar />
      <article>
        <section className="relative pt-24 md:pt-28 pb-16 md:pb-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">Litbuy Spreadsheet Pros and Cons</span>
            </nav>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Review</span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
              Litbuy Spreadsheet Pros and Cons: Honest Review
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              A balanced assessment of Litbuy Spreadsheet strengths and limitations. See how it performs on ease of use, features, scalability, and overall value for e-commerce sellers.
            </p>
          </div>
        </section>

        <section className="relative py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto space-y-14">
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-100 rounded-2xl p-6">
                  <h2 className="font-heading text-lg font-semibold text-accent-600 mb-4 flex items-center gap-2"><i className="ri-thumb-up-line"></i> Pros</h2>
                  <ul className="flex flex-col gap-3">
                    <li className="flex items-start gap-2 text-sm text-foreground-700"><i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>Purpose-built for e-commerce workflows, not generic spreadsheets</li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700"><i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>Pre-built templates save hours of manual setup</li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700"><i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>Clean interface with minimal learning curve</li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700"><i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>Automatic calculations eliminate formula errors</li>
                  </ul>
                </div>
                <div className="bg-background-100 rounded-2xl p-6">
                  <h2 className="font-heading text-lg font-semibold text-foreground-500 mb-4 flex items-center gap-2"><i className="ri-information-line"></i> Limitations</h2>
                  <ul className="flex flex-col gap-3">
                    <li className="flex items-start gap-2 text-sm text-foreground-700"><i className="ri-subtract-line text-foreground-400 mt-0.5 flex-shrink-0"></i>May require adjustment period for users accustomed to Excel</li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700"><i className="ri-subtract-line text-foreground-400 mt-0.5 flex-shrink-0"></i>Advanced customizations may need support team assistance</li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700"><i className="ri-subtract-line text-foreground-400 mt-0.5 flex-shrink-0"></i>Best suited for e-commerce rather than general business use</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-10 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">Decide for Yourself</h2>
              <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">Visit the official store and experience Litbuy Spreadsheet firsthand.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors">Visit Main Store <i className="ri-arrow-right-up-line"></i></a>
                <Link to="/" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors">Back to Home <i className="ri-home-4-line"></i></Link>
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-8">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">Continue Reading</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/litbuy-spreadsheet-alternatives" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Best Alternatives</Link>
                <Link to="/litbuy-spreadsheet-guide" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Complete Guide</Link>
                <Link to="/" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Home</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </main>
  );
}